import { TaskStatus } from '../routine/enums';
import type { DailyStep } from '../routine/models';
import type { DailyStepId } from '../shared/ids';

export interface DailyProgress {
  readonly percentage: number;
  readonly earnedWeight: number;
  readonly totalWeight: number;
  readonly completedCount: number;
  readonly activeCount: number;
  readonly nextStepId?: DailyStepId;
}

const acquiredFraction = {
  [TaskStatus.pending]: 0,
  [TaskStatus.inProgress]: 0.25,
  [TaskStatus.completed]: 1,
  [TaskStatus.skipped]: 0,
  [TaskStatus.postponed]: 0,
} as const;

export function calculateDailyProgress(steps: readonly DailyStep[]): DailyProgress {
  const orderedSteps = [...steps].sort((first, second) => first.order - second.order);
  const totalWeight = orderedSteps.reduce((total, step) => total + (step.required ? 2 : 1), 0);
  const earnedWeight = orderedSteps.reduce(
    (total, step) => total + (step.required ? 2 : 1) * acquiredFraction[step.status],
    0,
  );
  const nextStep = orderedSteps.find(
    (step) => step.status === TaskStatus.pending || step.status === TaskStatus.inProgress,
  );

  return {
    percentage: totalWeight === 0 ? 0 : Math.round((earnedWeight / totalWeight) * 100),
    earnedWeight,
    totalWeight,
    completedCount: orderedSteps.filter((step) => step.status === TaskStatus.completed).length,
    activeCount: orderedSteps.length,
    ...(nextStep ? { nextStepId: nextStep.id } : {}),
  };
}

/** Code semantique stable — le domaine ne produit jamais de texte localise. */
export type ProgressMessageCode = 'idle' | 'launched' | 'continuing' | 'advancing' | 'solid' | 'ascended';

const PROGRESS_MESSAGE_THRESHOLDS: readonly { max: number; code: ProgressMessageCode }[] = [
  { max: 1, code: 'idle' },
  { max: 25, code: 'launched' },
  { max: 50, code: 'continuing' },
  { max: 75, code: 'advancing' },
  { max: 100, code: 'solid' },
];

/** Retourne un code semantique stable — jamais une chaine localisee.
 *  La couche presentation traduit le code via le catalogue i18n. */
export function getProgressMessageCode(percentage: number): ProgressMessageCode {
  for (const { max, code } of PROGRESS_MESSAGE_THRESHOLDS) {
    if (percentage < max) return code;
  }
  return 'ascended';
}
